import { useEffect, useRef } from 'react'

import { useSessionPricing } from 'dataQueries/purchaseFlowRevampSessionPricing.query'
import type { SessionPricingV2DTO } from 'models/purchaseFlowRevamp'
import { useEmbedIsOneSystem } from 'utils/embedSession/useEmbedIsOneSystem'
import { logger } from 'utils/logging'

import { PfRevampEventName } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'
import { usePfEmbed } from '../routing/usePfEmbed'
import { repricedCartCounts, repriceExtrasFromPricing, repriceItemsFromPricing } from './oneSystemPricing'
import { nextQuotaPreview } from './quotaPreview'
import { usePfStore } from './store'

/**
 * Keeps a OneSystem cart priced the way the order will be charged.
 *
 * The catalogue quotes the OneSystem price book, but the IV24 quota makes packs of up to 20 photos free
 * per property — and which pack is free depends on the whole cart. The backend answers that through
 * `…/sessions/{id}/pricing`; this writes each line's unit, struck price and coverage onto its cart item,
 * so the cart, the summary, `useIsFreeCart` and the payment intent all read the same numbers. The
 * instruction extras' fee lines are laid onto `extrasFromInstructions` the same way.
 *
 * Asked only inside a OneSystem embed, and applied only when the answer says the session IS OneSystem
 * (`oneSystemQuota` present): the session decides, the embed flag only saves the request elsewhere.
 * An answer for an earlier cart is never applied — a stale price on the wrong pack is worse than a
 * moment on the catalogue price.
 */
export function useOneSystemPricingSync(): void {
  const { isEmbed } = usePfEmbed()
  const isOneSystem = useEmbedIsOneSystem()
  const { data, owners, isStale } = useSessionPricing(isEmbed && isOneSystem)

  useEffect(() => {
    const { draft, quotaPreview, setQuotaPreview } = usePfStore.getState()
    const next = nextQuotaPreview({ hasLines: Boolean(owners), data, isStale, sessionId: draft.sessionId })
    if (next === 'keep' || next === quotaPreview) return
    setQuotaPreview(next)
  }, [data, owners, isStale])

  // The answer last reported: writing it onto the cart re-runs the effect below with the same answer.
  const reportedRef = useRef<SessionPricingV2DTO | null>(null)

  useEffect(() => {
    if (!data?.oneSystemQuota || !owners || isStale) return

    const state = usePfStore.getState()
    const repriced = repriceItemsFromPricing(state.items, owners, data.lines)
    if (data !== reportedRef.current && Object.keys(repriced).length > 0) {
      reportedRef.current = data
      // The event must never stop the prices below from reaching the cart.
      try {
        dispatchPfRevampEvent({ type: PfRevampEventName.CART_REPRICED, payload: repricedCartCounts(state.items, repriced) })
      } catch (error) {
        logger.error('Embed reprice event could not be computed', error, {
          context: { component: 'useOneSystemPricingSync', action: 'embedRepricedEventFailed' },
        })
      }
    }
    for (const item of state.items) {
      const next = repriced[item.id]
      const current = item.priceSnapshot
      if (!next || !current) continue
      if (
        current.unitPrice === next.unitPrice &&
        (current.baseUnitPrice ?? null) === next.baseUnitPrice &&
        (current.addonsPrice ?? null) === next.addonsPrice &&
        (current.baseAddonsPrice ?? null) === next.baseAddonsPrice &&
        current.quotaCovered === next.quotaCovered
      ) continue

      const { id, ...rest } = item
      state.replaceItem(id, {
        ...rest,
        priceSnapshot: {
          ...current,
          unitPrice: next.unitPrice,
          baseUnitPrice: next.baseUnitPrice,
          addonsPrice: next.addonsPrice,
          baseAddonsPrice: next.baseAddonsPrice,
          quotaCovered: next.quotaCovered,
          resolvedAt: new Date().toISOString(),
        },
      })
    }

    const extraPrices = repriceExtrasFromPricing(state.extrasFromInstructions, owners, data.lines)
    if (extraPrices) state.applyExtrasSessionPricing(extraPrices)
  }, [data, owners, isStale])
}
